import { actionTypes, useUserState } from '@common/context/userContext';
import environment from '@common/relay/environment';
import createOrSaveUser, {
    CreateOrSaveUserMutation,
} from '@containers/Consignments/mutations/createOrSaveUserMutation';
import { userDetailsQuery } from '@containers/Consignments/queries/userDetailsQuery';
import { accounts, IUserClaims } from '@typings';
import { useState } from 'react';
import { useMutation } from 'relay-hooks';
import { fetchQuery, Observable } from 'relay-runtime';

interface UserClaimsDetails {
    auth0Id: string;
    email: string;
    firstName: string;
    lastName: string;
    mobilePhone: string;
    defaultAccount: string;
    accounts: {
        accountId: string;
        accountLabel: string;
        accountType: string;
        roles: [];
    }[];
}
// Initialize a empty user details with no role for user to see the main page
// when they are able to login but failed when get userDetails
const handleSignInError = (): Observable<{ userDetails: UserClaimsDetails }> => {
    const emptyUserClaims = {
        auth0Id: '',
        email: '',
        firstName: 'User',
        lastName: '',
        mobilePhone: '',
        defaultAccount: '',
        signature: '',
        accounts: [
            {
                accountId: '',
                accountLabel: '',
                accountType: '',
                roles: [],
            },
        ],
    };

    const userDetails = emptyUserClaims;
    return Observable.create((observer: any) => {
        observer.next({ userDetails: userDetails });
        observer.complete();
    });
};

const useUserDetails = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const [, dispatch] = useUserState();
    const [error, setError] = useState(null);
    const [mutate] = useMutation(CreateOrSaveUserMutation);

    const fetchUserDetails = async (accessToken: string) => {
        setIsLoading(true);
        setError(null);
        try {
            let userDetailsResponse: any = await fetchQuery(environment, userDetailsQuery, {})
                .toPromise()
                .catch(() => handleSignInError().toPromise());
            if (!userDetailsResponse?.userDetails) {
                userDetailsResponse = await handleSignInError().toPromise();
            }

            setIsNewUser(userDetailsResponse.userDetails.isNewUser);
            if (userDetailsResponse) {
                const rawClaims = userDetailsResponse.userDetails;
                const account = getDefaultAccount(rawClaims);
                const claims = normalizeClaimsWhenDefaultAccountMissing(rawClaims, account);
                dispatch({
                    type: actionTypes.user.setUser,
                    value: {
                        token: accessToken,
                        claims,
                        accountDetails: account,
                    },
                });
            }
            //   getDetails(userDetails);
        } catch (error: any) {
            setError(error.message || 'Something went wrong!');
        }
        setIsLoading(false);
    };

    const setUserSignature = async (userData: any, signature: string) => {
        const response = await createOrSaveUser(mutate, { ...userData, signature: signature });
        dispatch({
            type: actionTypes.user.setUserClaims,
            value: { claims: response, isRegister: false },
        });
    };

    const setTermsAccepted = async (userData: any, termsAccepted: boolean) => {
        const response = await createOrSaveUser(mutate, { ...userData, termsAccepted: termsAccepted });
        dispatch({
            type: actionTypes.user.setUserClaims,
            value: { claims: response, isRegister: true },
        });
    };

    return {
        isLoading,
        error,
        fetchUserDetails,
        isNewUser,
        setUserSignature,
        setTermsAccepted,
    };
};

export function getDefaultAccount(claims: IUserClaims, register?: boolean): accounts {
    if (!claims?.accounts?.length) {
        return {} as accounts;
    }

    // ignore gettting selected account from session storage if it coming from registration process
    if (!register) {
        const selectedAccountBase = sessionStorage.getItem('_xenvd');
        //If user has selected an account, that must still be selected on reload
        const selectedAccount = getSelectedAccount(claims.accounts, selectedAccountBase);
        if (selectedAccount) return selectedAccount;
    }

    let account = claims.defaultAccount
        ? claims.accounts.find((acc) => acc.envdAccountId === claims.defaultAccount)
        : undefined;

    if (!account) {
        account = claims.accounts[0];
    }

    return account;
}

/** When API defaultAccount does not match any account, align claims.defaultAccount with the resolved account (first in list). Skips if session storage already selected an account. */
export function normalizeClaimsWhenDefaultAccountMissing(claims: IUserClaims, resolvedAccount: accounts): IUserClaims {
    if (!claims.accounts?.length || !resolvedAccount?.envdAccountId) {
        return claims;
    }
    const selectedAccountBase = sessionStorage.getItem('_xenvd');
    if (getSelectedAccount(claims.accounts, selectedAccountBase)) {
        return claims;
    }
    const defaultMatches = claims.accounts.some((a) => a.envdAccountId === claims.defaultAccount);
    if (defaultMatches) {
        return claims;
    }
    return { ...claims, defaultAccount: resolvedAccount.envdAccountId };
}

function getSelectedAccount(accounts: accounts[], selectedAccountBase: string | null) {
    if (selectedAccountBase && accounts) {
        const selectedAccount = JSON.parse(atob(selectedAccountBase ?? '{}')).id;
        if (selectedAccount) {
            return accounts.find((acc: any) => acc?.envdAccountId === selectedAccount);
        }
    }
    return null;
}

export default useUserDetails;
